import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, ClientDTO, UserFullDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { UtilService } from 'src/app/services/util.service';
import { AdminClientAdminAddModalComponent } from '../admin-client-admin-add-modal/admin-client-admin-add-modal.component';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { SbSplitButton } from '../../../../services/global/global-interfaces';

type SplitButtonAction = 'removeAdmin';

@Component({
  selector: 'sb-comp-admin-client-admin-management-modal',
  templateUrl: './admin-client-admin-management-modal.component.html',
  styleUrls: ['./admin-client-admin-management-modal.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminClientAdminManagementModalComponent extends RequestClass implements OnInit {
  public readonly util = inject(UtilService);
  public readonly errorhandler = inject(ErrorService);
  private readonly api = inject(API);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  client: ClientDTO = undefined;

  // table wrapper variables
  admins: UserFullDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit(): void {
    this.client = this.modalNavigation.getCurrentNavigation().data?.client;
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      ._admin_Client_retrieveAdmins(
        this.client?.id,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (userAdmins: UserFullDTO[]) => {
          this.admins = this.buildSplitButtons(userAdmins);
        },
        error: error => {
          this.errorhandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  addAdmin() {
    this.modalNavigation.navigate(AdminClientAdminAddModalComponent, { client: this.client });
  }

  removeAdmin(user: UserFullDTO) {
    this.loading.set(true);
    this.api
      ._admin_Client_removeAdmin(this.client?.id, user.id)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: () => {
          this.requestDebounced();
        },
      });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'minus',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'removeAdmin',
        tooltip: 'lang.globals.revokeAdmin',
      });
    });
    return result;
  }

  resolveSplitButtonAction(user: UserFullDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'removeAdmin':
        this.removeAdmin(user);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
